import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Users, TrendingUp, Truck, Store, DollarSign, Network, Award, Rocket } from "lucide-react";
import EarningsSimulator from "@/components/EarningsSimulator";
import Footer from "@/components/Footer";
import LiveStatsWidget from "@/components/LiveStatsWidget";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 py-4">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="h-12 w-12" />
            <span className="text-2xl font-bold text-white font-heading">GoFood</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pre-launch" className="text-white hover:text-white/80 font-medium flex items-center gap-1">
              <Rocket className="w-4 h-4" />
              Pré-Lançamento
            </Link>
            <Link to="/login" className="text-white hover:text-white/80 font-medium">
              Login
            </Link>
            <Button
              asChild
              className="bg-white text-primary hover:bg-gray-100 rounded-full px-6"
            >
              <Link to="/register/FUNDADOR" onClick={() => window.gofoodTrack?.("open_register", { from: "nav" })}>Cadastrar</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="hero-gradient relative overflow-hidden">
        <div className="container mx-auto px-6 py-28 pt-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex justify-center mb-6">
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="h-24 w-24 animate-float" />
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-extrabold mb-6" data-testid="hero-title">
              Peça, Entregue & Ganhe
            </h1>
            <p className="text-xl md:text-2xl mb-8 font-body opacity-95">
              A primeira plataforma de delivery com sistema multinível de indicações. 
              Transforme cada pedido em oportunidade de renda!
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                asChild
                size="lg"
                className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-full"
                data-testid="cta-register-button"
              >
                <Link to="/register/FUNDADOR" onClick={() => window.gofoodTrack?.("open_register", { from: "hero_cta" })}>Começar Agora</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-semibold px-8 py-6 text-lg rounded-full"
                data-testid="login-button"
              >
                <Link to="/login">Já tenho conta</Link>
              </Button>
            </div>

            {/* Contadores ao vivo */}
            <div className="mt-8 flex justify-center" data-testid="landing-live-stats">
              <div className="rounded-full bg-zinc-900/80 backdrop-blur-md border border-white/15 shadow-xl px-4 py-2">
                <LiveStatsWidget variant="inline" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-10 right-20 w-40 h-40 bg-white rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center" data-testid="stat-categories">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">7+</div>
              <div className="text-gray-600 font-body">Categorias</div>
            </div>
            <div className="text-center" data-testid="stat-commission">
              <div className="text-4xl md:text-5xl font-bold text-secondary mb-2">7,5%</div>
              <div className="text-gray-600 font-body">Comissão em Vendas</div>
            </div>
            <div className="text-center" data-testid="stat-merchant-bonus">
              <div className="text-4xl md:text-5xl font-bold text-accent mb-2">40%</div>
              <div className="text-gray-600 font-body">Bônus Lojista</div>
            </div>
            <div className="text-center" data-testid="stat-levels">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">5</div>
              <div className="text-gray-600 font-body">Níveis MLM</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-center mb-16">
            Como Funciona?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl card-hover" data-testid="feature-user">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <ShoppingBag className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Para Usuários</h3>
              <p className="text-gray-600 font-body mb-4">
                Faça pedidos em restaurantes, farmácias, pet shops e mais. Ganhe comissões indicando amigos!
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Entrega rápida e segura
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Ganhe com sua rede (5 níveis)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Bônus especial por lojista indicado
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl card-hover" data-testid="feature-merchant">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6">
                <Store className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Para Lojistas</h3>
              <p className="text-gray-600 font-body mb-4">
                Gerencie seu negócio com planos acessíveis. Adicione produtos, receba pedidos em tempo real.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary rounded-full"></div>
                  3 planos disponíveis
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary rounded-full"></div>
                  Dashboard completo
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-secondary rounded-full"></div>
                  Gestão de produtos ilimitados
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl card-hover" data-testid="feature-driver">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6">
                <Truck className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Para Entregadores</h3>
              <p className="text-gray-600 font-body mb-4">
                Aceite entregas, defina seu horário e ganhe por cada entrega realizada.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  Flexibilidade de horários
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  Pagamento imediato
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                  Rotas otimizadas
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* MLM Section */}
      <div className="py-20 mlm-gradient relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Award className="w-20 h-20 mx-auto mb-6 opacity-90" />
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Sistema Multinível Inteligente
            </h2>
            <p className="text-xl mb-12 opacity-95 font-body">
              Ganhe comissões em até 5 níveis da sua rede de indicações.
              7,5% do valor distribuído automaticamente + Taxa de plataforma de 15%!
            </p>
            
            <div className="grid md:grid-cols-5 gap-4 mb-12">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="level-badge level-1 mx-auto mb-3">1</div>
                <div className="font-bold text-2xl mb-1">3%</div>
                <div className="text-sm opacity-80">Nível Direto</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="level-badge level-2 mx-auto mb-3">2</div>
                <div className="font-bold text-2xl mb-1">2,4%</div>
                <div className="text-sm opacity-80">Segundo Nível</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="level-badge level-3 mx-auto mb-3">3</div>
                <div className="font-bold text-2xl mb-1">1,5%</div>
                <div className="text-sm opacity-80">Terceiro Nível</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="level-badge level-4 mx-auto mb-3">4</div>
                <div className="font-bold text-2xl mb-1">0,9%</div>
                <div className="text-sm opacity-80">Quarto Nível</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <div className="level-badge level-5 mx-auto mb-3">5</div>
                <div className="font-bold text-2xl mb-1">0,2%</div>
                <div className="text-sm opacity-80">Quinto Nível</div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-md p-8 rounded-2xl">
              <h3 className="font-heading text-2xl font-bold mb-4">Bônus Especial: Indicação de Lojistas</h3>
              <p className="text-lg mb-4">Ganhe 40% do valor do plano mensal quando indicar um lojista!</p>
              <div className="bg-white/10 p-6 rounded-xl text-center">
                <div className="font-bold text-lg mb-2">Plano Único - R$ 199/mês</div>
                <div className="text-4xl font-bold text-accent mb-2">R$ 79,60</div>
                <div className="text-sm opacity-80">por cada lojista indicado</div>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm">+ Taxa de 15% por venda do lojista</p>
                  <p className="text-xs opacity-80 mt-1">(50% para plataforma, 50% distribuído na rede)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-center mb-16">
            Explore por Categoria
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: "Restaurantes", image: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=800&q=80" },
              { name: "Lanchonetes", image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80" },
              { name: "Açaí", image: "https://images.unsplash.com/photo-1553530666-ba11a7da3888?auto=format&fit=crop&w=800&q=80" },
              { name: "Sushi", image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=800&q=80" },
              { name: "Farmácia", image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=800&q=80" },
              { name: "Pet Shop", image: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=800&q=80" },
              { name: "Supermercado", image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80" },
              { name: "Padaria", image: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=800&q=80" },
            ].map((category) => (
              <div
                key={category.name}
                className="relative h-48 rounded-2xl overflow-hidden card-hover cursor-pointer group"
                data-testid={`category-${category.name.toLowerCase()}`}
              >
                <img
loading="lazy" decoding="async"                   src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                  <h3 className="font-heading text-white text-xl font-bold">{category.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Earnings Simulator */}
      <EarningsSimulator />

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-primary to-orange-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            Comece Hoje Mesmo!
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto font-body">
            Cadastre-se gratuitamente e faça parte da revolução do delivery com ganhos recorrentes.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-primary hover:bg-gray-100 font-semibold px-12 py-6 text-lg rounded-full"
            data-testid="footer-cta-button"
          >
            <Link to="/register/FUNDADOR" onClick={() => window.gofoodTrack?.("open_register", { from: "footer_cta" })}>Criar Minha Conta</Link>
          </Button>
        </div>
      </div>

      {/* Footer (dinâmico, editável em /admin/settings) */}
      <Footer />
    </div>
  );
};

export default LandingPage;
